import type { ChartOptions } from 'chart.js';

export const CHART_COLORS = {
  blue: '#3182ce',
  green: '#38a169',
  purple: '#805ad5',
  orange: '#ed8936',
  red: '#e53e3e',
  yellow: '#d69e2e',
  teal: '#319795',
  pink: '#d53f8c',
  gray: '#a0aec0',
};

export const CHART_PALETTE = [
  CHART_COLORS.blue,
  CHART_COLORS.green,
  CHART_COLORS.purple,
  CHART_COLORS.orange,
  CHART_COLORS.teal,
  CHART_COLORS.pink,
  CHART_COLORS.yellow,
  CHART_COLORS.red,
];

export const STATUS_COLORS: Record<string, string> = {
  completed: CHART_COLORS.green,
  'in progress': CHART_COLORS.blue,
  pending: CHART_COLORS.orange,
  'not started': CHART_COLORS.gray,
  'on hold': CHART_COLORS.red,
  cancelled: CHART_COLORS.pink,
};

export const PRIORITY_COLORS: Record<string, string> = {
  urgent: CHART_COLORS.red,
  high: CHART_COLORS.orange,
  medium: CHART_COLORS.yellow,
  low: CHART_COLORS.green,
};

export const baseLegend = {
  position: 'bottom' as const,
  labels: {
    padding: 16,
    usePointStyle: true,
    pointStyle: 'circle' as const,
    font: { size: 12 },
  },
};

export const baseTooltip = {
  backgroundColor: 'rgba(26, 32, 44, 0.92)',
  padding: 12,
  cornerRadius: 8,
  titleFont: { size: 13, weight: 'bold' as const },
  bodyFont: { size: 12 },
};

export const doughnutOptions: ChartOptions<'doughnut'> = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '68%',
  plugins: {
    legend: baseLegend,
    tooltip: baseTooltip,
  },
};

export const barOptions: ChartOptions<'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: baseTooltip,
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: '#718096', font: { size: 11 } },
    },
    y: {
      beginAtZero: true,
      grid: { color: 'rgba(0,0,0,0.06)' },
      ticks: { color: '#718096', precision: 0, font: { size: 11 } },
    },
  },
};

export const horizontalBarOptions: ChartOptions<'bar'> = {
  ...barOptions,
  indexAxis: 'y',
  scales: {
    x: {
      beginAtZero: true,
      grid: { color: 'rgba(0,0,0,0.06)' },
      ticks: { color: '#718096', precision: 0, font: { size: 11 } },
    },
    y: {
      grid: { display: false },
      ticks: { color: '#4a5568', font: { size: 11 } },
    },
  },
};

export const lineOptions: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: baseLegend,
    tooltip: baseTooltip,
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: '#718096', maxRotation: 0, autoSkip: true, maxTicksLimit: 7, font: { size: 11 } },
    },
    y: {
      beginAtZero: true,
      grid: { color: 'rgba(0,0,0,0.06)' },
      ticks: { color: '#718096', precision: 0, font: { size: 11 } },
    },
  },
};

export const formatLabel = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

export const getId = (ref?: string | { _id: string } | null) => {
  if (!ref) return '';
  return typeof ref === 'string' ? ref : ref._id;
};

export const isCompletedStatus = (status?: string) => status?.toLowerCase() === 'completed';

export const toDateKey = (value?: string | Date) => {
  if (!value) return '';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};
